import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { AppState } from '@root/app-state.module';
import { select, Store } from '@ngrx/store';
import { selectCardsAttributes, selectSearchFieldsArray } from '@store/user/user.selector';
import { combineLatest, Observable } from 'rxjs';
import { DataElement, DataType } from '@store/user/user.model';
import { FieldCode } from '@root/search/models/display-fields.constant';
import { map, take } from 'rxjs/operators';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectAllPropertyMobile, UpdateSearchResultSortingPreference } from '@store/search-board/search-board.actions';
import { selectSortingPreference } from '@store/search-board/search-board.selector';
import { INDICATOR_CONSTS, SearchResultsSortingPreference, Sorting } from '@store/search-board/search-board.model';
import { LabelValue } from '@root/search/models/map.models';

@UntilDestroy()
@Component({
  selector: 'rlst-property-sort-modal',
  templateUrl: './property-sort-modal.component.html',
  styleUrls: ['./property-sort-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PropertySortModalComponent implements OnInit {
  public sortAttributes$: Observable<Array<DataElement>>;
  public customizeForm: UntypedFormGroup;
  public allowedStatuses: UntypedFormArray;
  public readonly ALLOWED_STATUSES: Array<{ label: string, class: string, value: string }> = [
    {
      label: 'Subject Property',
      class: 'subject-property',
      value: INDICATOR_CONSTS.MLS_STD_MLS_STATUS_SUBJECT_PROPERTY
    },
    {
      label: 'Active',
      class: 'active',
      value: INDICATOR_CONSTS.MLS_STD_MLS_STATUS_ACTIVE
    },
    {
      label: 'Pending',
      class: 'pending',
      value: INDICATOR_CONSTS.MLS_STD_MLS_STATUS_PENDING
    },
    {
      label: 'Sold',
      class: 'sold',
      value: INDICATOR_CONSTS.MLS_STD_MLS_STATUS_SOLD
    },
    {
      label: 'Expired',
      class: 'expired',
      value: INDICATOR_CONSTS.MLS_STD_MLS_STATUS_EXPIRED
    },
    {
      label: 'Active Under Contract',
      class: 'active-under-contract',
      value: INDICATOR_CONSTS.MLS_STD_MLS_STATUS_ESCROW_SHOW

    },
    {
      label: 'Pending',
      class: 'pending',
      value: INDICATOR_CONSTS.MLS_STD_MLS_STATUS_ESCROW_NOT_SHOW

    }
  ];
  private customCardsAttributes$: Observable<Array<DataElement>>;
  private preDefinedCardsAttributes$: Observable<Array<DataElement>>;
  private readonly PREDEFINED_ATTRIBUTES_FIELDCODES: Array<FieldCode> = [
    FieldCode.ACTUAL_SALE_PRICE,
    FieldCode.LOT_SQUARE_FEET,
    FieldCode.TOTAL_BATHS,
    FieldCode.BEDROOMS
  ];

  constructor(private readonly dialogRef: MatDialogRef<PropertySortModalComponent>,
              private readonly store$: Store<AppState>) {
  }

  ngOnInit(): void {
    this.createCustomizeForm();
    this.preDefinedCardsAttributes$ = this.store$.pipe(select(selectSearchFieldsArray(this.PREDEFINED_ATTRIBUTES_FIELDCODES)));
    this.customCardsAttributes$ = this.store$.pipe(select(selectCardsAttributes));
    this.sortAttributes$ = combineLatest(
      [
        this.preDefinedCardsAttributes$,
        this.customCardsAttributes$
      ]
    ).pipe(
      map(([preDefinedAtt, customAtt]: [Array<DataElement>, Array<DataElement>]) => preDefinedAtt.concat(customAtt))
    );
  }

  public close(): void {
    this.dialogRef.close();
  }

  public apply(): void {
    this.store$.dispatch(new SelectAllPropertyMobile([]));
    this.store$.dispatch(new UpdateSearchResultSortingPreference(this.customizeForm.value));
    this.dialogRef.close();
  }

  private createCustomizeForm(): void {
    this.store$.pipe(
      select(selectSortingPreference),
      take(1)
    ).subscribe((pref: SearchResultsSortingPreference) => {
      this.customizeForm = new UntypedFormGroup({
        sortField: new UntypedFormControl(pref.sortField),
        sortOrder: new UntypedFormControl(pref.sortOrder),
        allowedStatuses: new UntypedFormArray(this.ALLOWED_STATUSES.map((status: { label: string, class: string, value: string }) =>
          new UntypedFormControl(pref.allowedStatuses.some((code: string) => code === status.value) ? status.value : null)
        )),
        showOnlyWithPhoto: new UntypedFormControl(pref.showOnlyWithPhoto)
      });
      this.allowedStatuses = this.customizeForm.controls['allowedStatuses'] as UntypedFormArray;
    });
    this.customizeForm.get('sortField').valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe((field: string | FieldCode) => {
      if (field === '') {
        this.customizeForm.get('sortOrder').setValue('');
      }
    });
  }

  public toggleStatus({target}: { target: HTMLInputElement }, i: number): void {
    this.allowedStatuses.controls[i].setValue(target.checked ? this.ALLOWED_STATUSES[i].value : null);
  }

  public getDefaultOption(dataType: DataType): LabelValue {
    if (dataType === DataType.DATE) {
      return {value: Sorting.OldestToNewest, label: Sorting.OldestToNewest};
    }
    if (dataType === DataType.NUMBER) {
      return {value: Sorting.LowestToHighest, label: Sorting.LowestToHighest};
    }
    if (dataType === DataType.STRING) {
      return {value: Sorting.AtoZ, label: Sorting.AtoZ};
    }
  }

  public getSwitchedOption(dataType: DataType): LabelValue {
    if (dataType === DataType.DATE) {
      return {value: Sorting.NewestToOldest, label: Sorting.NewestToOldest};
    }
    if (dataType === DataType.NUMBER) {
      return {value: Sorting.HighestToLowest, label: Sorting.HighestToLowest};
    }
    if (dataType === DataType.STRING) {
      return {value: Sorting.ZtoA, label: Sorting.ZtoA};
    }
  }
}
